import { Select } from '@99designs/common/components/Select';
import { DetailsPageUploader } from '__generated__/DetailsPageUploader';
import {
    SquareOnlineStoreBriefFields_inspirationFiles,
    SquareOnlineStoreBriefFields_referenceFiles,
} from '__generated__/SquareOnlineStoreBriefFields';
import { SquareOnlineStoreExistingAssets, SquareOnlineStoreProjectType } from '__generated__/types';
import CheckboxList from 'common/components/CheckboxList';
import { handlePromise } from 'common/utils/Bugsnag';
import DescriptionField from 'projects/components/BriefFields/DescriptionField';
import InspirationFilesField from 'projects/components/BriefFields/InspirationFilesField';
import InspirationUrlsField from 'projects/components/BriefFields/InspirationUrlsField';
import LanguageField from 'projects/components/BriefFields/LanguageField';
import ReferenceFilesField from 'projects/components/BriefFields/ReferenceFilesField';
import TitleField from 'projects/components/BriefFields/TitleField';
import { ObservableField as Field, ObservableForm } from 'projects/components/ObservableForm';
import { trackViewedBriefEvent } from 'projects/events';
import React, { useEffect } from 'react';

interface Fields {
    language: string;
    title: string;
    description: string;
    projectType: SquareOnlineStoreProjectType;
    referenceFiles: SquareOnlineStoreBriefFields_referenceFiles[];
    inspirationFiles: SquareOnlineStoreBriefFields_inspirationFiles[];
    inspirationUrls: string[];
    existingAssets: SquareOnlineStoreExistingAssets[];
}

export interface Props<T> {
    form: ObservableForm<T>;
    brief: {
        id: string;
        uploader: DetailsPageUploader;
    };
}

const DetailsPage: <T extends Fields>(props: Props<T>) => React.ReactElement<Props<T>> = ({
    form,
    brief,
}) => {
    useEffect(() => {
        trackViewedBriefEvent({
            id: brief.id,
            page: 'Details',
        });
    }, []);
    return (
        <div className="content-setting">
            <LanguageField form={form} />
            <TitleField
                form={form}
                placeholderText={__('E.g. Hip Florist needs fresh design for Square Online Store')}
            />

            <Field
                form={form}
                name="projectType"
                title={__('What best describes your need?')}
                required={true}
                render={() => (
                    <Select
                        name="projectType"
                        options={[
                            {
                                value: '',
                                disabled: true,
                                label: __('Select project type'),
                            },
                            {
                                value: SquareOnlineStoreProjectType.NEW_SITE,
                                label: __('Create a new Square Online Store'),
                            },
                            {
                                value: SquareOnlineStoreProjectType.RECREATE_SITE,
                                label: __('Migrate an existing site to Square Online Store'),
                            },
                            {
                                value: SquareOnlineStoreProjectType.MINOR_CHANGES,
                                label: __('Make minor changes to a Square Online Store'),
                            },
                            {
                                value: SquareOnlineStoreProjectType.MAJOR_CHANGES,
                                label: __(
                                    'Redesign or make major changes to a Square Online Store'
                                ),
                            },
                        ]}
                        value={form.get('projectType')}
                        onChange={({ value }) => {
                            handlePromise(
                                form.set('projectType', value as SquareOnlineStoreProjectType),
                                'DetailsPage'
                            );
                        }}
                        variations={['max']}
                    />
                )}
            />

            <DescriptionField
                form={form}
                placeholderText={__(
                    "E.g. I'm a florist and my shop is called Petal to the Metal. I'm trying to get my site up and going using Square Online Store and I need some help. I have about 40 different products that I sell and even offer delivery. I've put a lot of care into my brand and I like my site to be clean and easy to use while still conveying my unique brand."
                )}
            />
            <ReferenceFilesField form={form} uploader={brief.uploader} />
            <InspirationFilesField form={form} uploader={brief.uploader} />
            <InspirationUrlsField form={form} />

            <Field
                form={form}
                name="existingAssets"
                title={__('Do you have any of the following?')}
                render={() => (
                    <CheckboxList
                        options={[
                            {
                                value: SquareOnlineStoreExistingAssets.LOGO_AND_BRANDING,
                                label: __('Logo & Branding'),
                            },
                            {
                                value: SquareOnlineStoreExistingAssets.IMAGES_AND_VISUAL_ASSETS,
                                label: __('Images & Visual Assets'),
                            },
                            {
                                value: SquareOnlineStoreExistingAssets.WEBSITE_AND_PRODUCT_COPY,
                                label: __('Website & Product Copy'),
                            },
                        ]}
                        {...form.attach('existingAssets')}
                    />
                )}
            />
        </div>
    );
};

export default DetailsPage;
